import React, { useState, useMemo } from 'react'
import {
  Paper, Box, Typography, Chip, Divider, CircularProgress
} from '@material-ui/core'
import axios from 'axios'
import AttributeForm from './AttributeForm'
import RateImpactDisplay from './RateImpactDisplay'

const ServiceDetailPanel = ({ service, services, projectId, url, onUpdate }) => {
  const [saving, setSaving] = useState(false)

  // Find service metadata
  const meta = useMemo(() => {
    return services.find(s => s.serviceId === service.serviceId) || {}
  }, [services, service.serviceId])

  // Use first line item's attributes as the default (they should all share the same in V1)
  const firstLineItem = service.lineItems[0]
  const currentAttrs = firstLineItem?.serviceAttributes || {}

  const handleSaveAttributes = async (newValues) => {
    setSaving(true)
    try {
      // Save attributes to all line items for this service
      await Promise.all(
        service.lineItems.map(li =>
          axios.put(`${url}/api/bid-estimation/line-items/${li._id}/service-attributes`, {
            serviceAttributes: newValues
          })
        )
      )

      // Recalculate all components for this service
      await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/recalculate-service/${service.serviceId}`
      )

      if (onUpdate) onUpdate()
    } catch (err) {
      console.error('Save attributes error:', err)
      alert('Failed to save: ' + (err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const fmt = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

  return (
    <Paper variant="outlined" style={{ padding: 16 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="h6" style={{ flex: 1, fontSize: 16 }}>
          {meta.name || service.serviceId}
        </Typography>
        <Chip label={meta.category || ''} size="small" variant="outlined" />
      </Box>

      {/* Summary */}
      <Box mb={2}>
        <Typography variant="body2" color="textSecondary">
          {service.lineItems.length} line item{service.lineItems.length !== 1 ? 's' : ''} · Total qty: {service.totalQty}
        </Typography>
        <Typography variant="body2">
          Cost: {fmt(service.totalCost)} · Sell: {fmt(service.totalSell)}
        </Typography>
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {/* Attributes */}
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
        Attributes
      </Typography>
      {saving ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <AttributeForm
          serviceId={service.serviceId}
          currentValues={currentAttrs}
          url={url}
          onSave={handleSaveAttributes}
        />
      )}

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      {/* Rate Impact */}
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
        Rate Adjustments
      </Typography>
      <RateImpactDisplay
        serviceId={service.serviceId}
        attributeValues={currentAttrs}
        url={url}
      />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      {/* Line Items */}
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
        Line Items
      </Typography>
      {service.lineItems.map(li => (
        <Box key={li._id} mb={1} p={1} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <Typography variant="body2" style={{ fontWeight: 500 }}>{li.description}</Typography>
          <Typography variant="caption" color="textSecondary">
            {li.quantity} {li.unit} · {fmt(li.totalCost)}
          </Typography>
        </Box>
      ))}
    </Paper>
  )
}

export default ServiceDetailPanel
